import React, { useCallback, useEffect, useState } from "react";
import { MessageCircle, RefreshCw, Search } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { InquiryCard, INQUIRY_STATUSES } from "./InquiryCard";
import { api } from "../api";

export const InquiryInbox = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const fail = (err) => {
    setItems([]);
    setError([401, 403].includes(err?.response?.status) ? "You do not have access to inquiries. Contact your administrator." : "Could not load inquiries. Please try again.");
  };
  const load = useCallback(async () => {
    setBusy(true);
    try { setItems(await api.getInquiries()); setError(""); }
    catch (err) { fail(err); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => {
    load();
    const refresh = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", refresh);
    const timer = setInterval(refresh, 30000);
    return () => { clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [load]);
  const shown = items.filter((item) => (filter === "All" || item.status === filter) && [item.id, item.name, item.phone, item.senderCountry, item.receiverCountry].join(" ").toLowerCase().includes(query.toLowerCase().trim()));
  return <section data-testid="inquiry-inbox" className="min-w-0">
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div><h2 className="font-display text-lg font-bold flex items-center gap-2"><MessageCircle className="w-5 h-5 text-orange-600" />Customer inquiries</h2><p data-testid="inquiry-inbox-count" className="text-sm text-neutral-500 mt-1">{items.length} total · {items.filter((i) => i.status === "New").length} new</p></div>
      <Button data-testid="inquiry-refresh-button" onClick={load} disabled={busy} variant="outline" className="gap-2"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />Refresh</Button>
    </div>
    <div className="flex flex-wrap gap-3 mb-6">
      <div className="relative flex-1 min-w-0 basis-52"><Search className="absolute top-3 left-3 w-4 h-4 text-neutral-500" /><Input data-testid="inquiry-inbox-search" aria-label="Search inquiries" placeholder="Search name, phone or country…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 bg-white" /></div>
      <select data-testid="inquiry-inbox-filter" aria-label="Filter inquiry status" value={filter} onChange={(e) => setFilter(e.target.value)} className="border rounded-lg bg-white h-10 px-3 text-sm max-w-full">
        {["All", ...INQUIRY_STATUSES].map((status) => <option data-testid={`inquiry-filter-${status.toLowerCase().replace(/ /g, "-")}`} key={status}>{status}</option>)}
      </select>
    </div>
    {error ? <div role="alert" data-testid="inquiry-inbox-error" className="text-red-700 border border-red-200 rounded-lg p-4">{error}</div>
      : busy && !items.length ? <p data-testid="inquiry-inbox-loading" role="status" className="py-12 text-neutral-500">Loading inquiries…</p>
      : shown.length ? <div className="space-y-4">{shown.map((inquiry) => <InquiryCard key={inquiry.id} inquiry={inquiry} onUpdated={(updated) => setItems((prev) => prev.map((i) => i.id === updated.id ? updated : i))} onDeleted={(delId) => setItems((prev) => prev.filter((i) => i.id !== delId))} onError={fail} />)}</div>
      : <div data-testid="inquiry-inbox-empty" className="text-center border-y border-black/10 py-16"><MessageCircle className="w-10 h-10 text-neutral-300 mx-auto mb-3" /><p className="text-neutral-600">{items.length ? "No inquiries match your search." : "No customer inquiries yet."}</p></div>}
  </section>;
};